import {
  makeStyles,
  Alert,
  Button,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogContent,
  FormSectionTitle,
  FormikInputField,
  FormikPhoneNumberInput,
  Grid,
  Hidden,
  LinearProgress,
  Spinner,
  Typography
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps } from '@paintscout/ui'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import type { AppDocsQuery } from '@paintscout/api'
import { AppDocsDocument, useUpdateUserMutation } from '@paintscout/api'
import * as Sentry from '@sentry/react'
import { FastField, Field, Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import type { OptionsDocument } from 'paintscout'
import React, { useState } from 'react'
import { useAuth } from 'react-auth0'
import { useHistory } from 'react-router-dom'
import PaintingLady from 'src/public/images/painting-lady.svg'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    maxWidth: '648px',
    minWidth: 'auto'
  },
  upload: {
    '& img': {
      maxHeight: 200
    }
  },
  img: {
    maxWidth: 140,
    margin: '50px auto',
    display: 'block'
  },
  imgSm: {
    maxWidth: 100
  },
  canvasButton: {
    justifyContent: 'left'
  },
  dropzone: {
    minHeight: 150
  },
  dialog: {
    // zIndex is 3 higher than a normal modal to make sure it covers intercom since this modal
    // appears above intercom in the DOM instead of below like the other modals
    zIndex: '2147483004 !important' as any
  },
  dialogContent: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      textAlign: 'center',
      justifyContent: 'center',
      alignItems: 'center'
    }
  },
  contentContainer: {
    [theme.breakpoints.down('md')]: {
      padding: '40px 0'
    }
  },
  progressBar: {
    height: '6px'
  },
  video: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100%'
  },
  videoWrapper: {
    height: 0,
    paddingTop: '56.25%',
    position: 'relative',
    width: '100%'
  },
  colorTitle: {
    margin: '0 0 16px 0'
  },
  spinner: {
    padding: '100px 0'
  }
}))

function PersonalInfo(props: { validationError: string }) {
  const { validationError } = props
  return (
    <>
      <Grid item xs={12}>
        <FormSectionTitle title={'Welcome to PaintScout!'} subTitle="We need to learn a little more about you first." />
      </Grid>
      {validationError && (
        <Grid item xs={12}>
          <Alert severity="error">{validationError}</Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <Field component={FormikInputField} fullWidth={true} name="firstName" label="First Name" required />
      </Grid>
      <Grid item xs={12}>
        <Field component={FormikInputField} fullWidth={true} name="lastName" label="Last Name" required />
      </Grid>
    </>
  )
}

function CompanyInfo(props: { options: OptionsDocument; firstName: string; validationError: string }) {
  const { options, firstName, validationError } = props
  return (
    <>
      <Grid item xs={12}>
        <FormSectionTitle title={`Nice to meet you, ${firstName}`} subTitle="Tell us about your company." />
      </Grid>
      {validationError && (
        <Grid item xs={12}>
          <Alert severity="error">{validationError}</Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <FastField fullWidth={true} label="Company Name" name="companyName" component={FormikInputField} required />
      </Grid>
      <Grid item xs={12}>
        <FastField
          fullWidth={true}
          label="Company Phone"
          name="companyPhone"
          separator={options?.options?.phoneNumbers?.separator}
          component={FormikPhoneNumberInput}
          required
        />
      </Grid>
    </>
  )
}

function Video() {
  const classes = useStyles()
  return (
    <>
      <Grid item xs={12}>
        <FormSectionTitle title={`Let's Get Rolling`} subTitle="Help Us Supercharge Your Estimating Process" />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.videoWrapper}>
          <video
            className={classes.video}
            autoPlay
            controls
            src="https://res.cloudinary.com/taptapquote/video/upload/v1620152673/paintscout/website/videos/ps-trial-202105.mp4"
          />
        </div>
      </Grid>
    </>
  )
}

export interface WelcomeSetupDialogProps extends DialogProps {}

export default function WelcomeSetupDialog(props: WelcomeSetupDialogProps) {
  const classes = useStyles(props)
  const { options, save: saveOptions } = useClientOptions()

  const [updateUser] = useUpdateUserMutation()
  const { user, preferences } = useUser()
  const { authenticate } = useAuth()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const [step, setStep] = useState(0)
  const [validationError, setValidationError] = useState('')

  function nextStep(values) {
    if (step === 0 && (!values.firstName || !values.lastName)) {
      setValidationError(`By sharing your name, we're able to better customize your PaintScout trial.`)
      return
    } else if (step === 1 && (!values.companyName || !values.companyPhone)) {
      setValidationError(
        `By sharing your company name & phone number, we're able to better customize your PaintScout trial.`
      )
      return
    }

    setValidationError('')

    if (step < 2) {
      setStep(step + 1)

      // scroll to top of dialog when stepping forward
      setTimeout(() => {
        const node = document.querySelector(`#content-top`)
        node.scrollIntoView({ behavior: 'smooth' })
      })
    }
  }

  function handleFinish() {
    enqueueSnackbar(`You're ready to go!`, { variant: 'success' })
    props.onClose(null, null)
    history.push(`/quotes/`)
  }

  return (
    <Dialog {...props} className={classes.dialog} data-testid="welcome-setup-dialog" fullWidth maxWidth="md">
      <Formik
        initialValues={{
          // prefill with whatever data we happen to have by this point
          companyName: options.options.companyName.value as string,
          companyCity: options.options.companyCity.value as string,
          companyProv: options.options.companyProv.value as string,
          companyPostal: options.options.companyPostal.value as string,
          companyAddress: options.options.companyAddress.value as string,
          companyPhone: options.options.companyPhone.value as string,
          firstName: user.user_metadata?.firstName,
          lastName: user.user_metadata?.lastName,
          phoneNumber: user.user_metadata?.phoneNumber,
          signature: preferences.signature,
          title: user.user_metadata?.title,
          logo: options.options.logo,
          primaryColor: options.options.primaryColor.value,
          secondaryColor: options.options.secondaryColor.value
        }}
        onSubmit={async (values) => {
          try {
            // confirm and save
            await saveOptions({
              options: {
                ...options,
                options: {
                  ...options.options,
                  companyName: { ...options.options.companyName, value: values.companyName },
                  companyCity: { ...options.options.companyCity, value: values.companyCity },
                  companyProv: { ...options.options.companyProv, value: values.companyProv },
                  companyPostal: { ...options.options.companyPostal, value: values.companyPostal },
                  companyAddress: { ...options.options.companyAddress, value: values.companyAddress },
                  companyPhone: { ...options.options.companyPhone, value: values.companyPhone },
                  logo: {
                    ...options.options.logo,
                    value: values.logo?.value ?? null,
                    cdnValue: values.logo?.cdnValue ?? null,
                    cloudinaryId: values.logo?.cloudinaryId ?? null,
                    fileType: values.logo?.fileType ?? null,
                    width: values.logo?.width,
                    height: values.logo?.height,
                    orientation: values.logo?.orientation
                  }
                }
              }
            })
            await updateUser({
              variables: {
                input: {
                  user_id: user.user_id,
                  user_metadata: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    phoneNumber: values.phoneNumber,
                    title: values.title
                  },
                  app_metadata: {
                    roles: user.app_metadata.roles
                    // needsOnboarding: false
                  },
                  preferences: {
                    ...preferences,
                    signature: values.signature ?? null
                  },
                  isOnboardingUpdate: true
                }
              },
              context: {
                onlineOnly: true
              },
              update(cache, { data }) {
                const appDocs = cache.readQuery<AppDocsQuery>({
                  query: AppDocsDocument
                })

                cache.writeQuery<AppDocsQuery>({
                  query: AppDocsDocument,
                  data: {
                    ...appDocs,
                    appDocs: {
                      ...appDocs.appDocs,
                      preferences: data.updateUser.preferences
                    }
                  }
                })
              }
            })
            await authenticate({ ignoreCache: true }).catch((e) => {
              console.error('Error while refreshing profile session:', e)
            })
          } catch (error) {
            Sentry.captureException(error)
            enqueueSnackbar(
              `Uh oh, it looks like something went wrong. Please try again, or contact us at support@paintscout.com`,
              { variant: 'error', autoHideDuration: 10000 }
            )
          }
        }}
      >
        {({ values, isSubmitting, handleSubmit }) => (
          <>
            {/* <PreviewColors /> */}
            <LinearProgress
              id="content-top"
              className={classes.progressBar}
              variant={'determinate'}
              value={((step + 1) / 3) * 100}
            />
            <DialogContent className={classes.dialogContent}>
              <Form id="welcome-form">
                <Grid
                  className={classes.contentContainer}
                  container
                  spacing={2}
                  alignItems={step !== 2 ? 'center' : 'flex-end'}
                >
                  {step !== 2 && (
                    <Hidden smDown>
                      <Grid item xs={false} md={step !== 2 ? 4 : 2} lg={step !== 2 ? 5 : 3}>
                        <img
                          className={step !== 2 ? classes.img : `${classes.img} ${classes.imgSm}`}
                          src={PaintingLady}
                        />
                      </Grid>
                    </Hidden>
                  )}
                  <Grid item xs={12} md={step !== 2 ? 7 : 12} lg={step !== 2 ? 6 : 12}>
                    <Grid container spacing={2}>
                      {step === 0 && <PersonalInfo validationError={validationError} />}
                      {step === 1 && (
                        <CompanyInfo options={options} validationError={validationError} firstName={values.firstName} />
                      )}
                      {step === 2 && <Video />}
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            </DialogContent>
            <DialogActions>
              {step === 2 ? (
                <>
                  <Grid item xs={12} container>
                    <Grid item container justifyContent="center" xs={12}>
                      {isSubmitting ? (
                        <>
                          <Grid container item justifyContent="center">
                            <Spinner data-testid="demo-spinner" size={20} />
                          </Grid>
                          <Grid container item justifyContent="center" alignContent="center">
                            <Typography variant="h6">Creating Account</Typography>
                          </Grid>
                        </>
                      ) : (
                        <Button
                          size="large"
                          onClick={() => {
                            handleFinish()
                          }}
                        >
                          Start Trial
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </>
              ) : (
                <DialogActionButton
                  form="welcome-form"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  variant="contained"
                  onClick={() => {
                    if (step === 1) {
                      nextStep(values)
                      handleSubmit()
                    } else {
                      nextStep(values)
                    }
                  }}
                >
                  Next
                </DialogActionButton>
              )}
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  )
}
